import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Skeleton } from 'antd'
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts'
import { fetchMonthStats } from '../../utils/helpers'
import { setMonthStats } from '../../Redux/actions/monthlyStatsAction'
import moment from 'moment'
import { dateFormate, translations } from '../../utils/constants'

const Charts = ({ language }) => {
    const dispatch = useDispatch()
    const user = useSelector(state => state?.authReducer?.user)
    const organizationDetails = useSelector(state => state?.authReducer?.organization)
    const organizationId = organizationDetails?.id
    const monthStats = useSelector(state => state?.monthlyStatsReducers?.monthStats)

    useEffect(() => {
        fetchMonthStats({ user, organizationId, dispatch, setMonthStats })
    }, [user, organizationId])

    const data = useMemo(() => {
        const months = Array.from({ length: 12 }, (_, i) => {
            const date = moment().subtract(11 - i, 'months') 
            const monthStr = date.format('YYYY-MM')
            const monthName = date.format('MMM').toLowerCase()

            return {
                monthKey: monthStr,
                name: translations[language]?.[`month${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`],
                label: date.format(dateFormate),
                value1: 0,
                value2: 0
            }
        })

        const vehiclesMap = new Map((monthStats?.vehicles || [])?.map(item => [item.month, parseInt(item.count)]))
        const devicesMap = new Map((monthStats?.devices || [])?.map(item => [item.month, parseInt(item.count)]))

        return months?.map(m => ({
            ...m,
            value1: vehiclesMap.get(m.monthKey) || 0,
            value2: devicesMap.get(m.monthKey) || 0
        }))
    }, [monthStats, language])

    if (!monthStats) {
        return (
            <div className='dasboard-charts' style={{ padding: '10px 20px' }}>
                <Skeleton active paragraph={{ rows: 5 }} />
            </div>
        )
    }

    return (
        <div className='dasboard-charts'>
            <ResponsiveContainer>
                <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id='vehiclesGradient' x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='0%' stopColor='#ED2328' stopOpacity={0.25} />
                            <stop offset='100%' stopColor='#ED2328' stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id='devicesGradient' x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='0%' stopColor='#40AFFF' stopOpacity={0.25} />
                            <stop offset='100%' stopColor='#40AFFF' stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (active && payload?.length) {
                                const info = payload[0]?.payload
                                return (
                                    <div style={{ background: '#fff', padding: '8px 12px', borderRadius: 8, boxShadow: 'rgba(99, 99, 99, 0.25) 0px 2px 8px 0px' }}>
                                        <p style={{ margin: 0, fontWeight: 600 }}>{info.label}</p>
                                        <p style={{ margin: '4px 0 0', color: '#ED2328' }}> {translations[language]?.vehicles} : {info.value1} </p>
                                        <p style={{ margin: '4px 0 0', color: '#40AFFF' }}> {translations[language]?.devices} : {info.value2} </p>
                                    </div>
                                )
                            }
                            return null
                        }}
                    />
                    <Legend
                        formatter={(value) =>
                            value === 'value1' ? translations[language]?.vehicles : translations[language]?.devices
                        }
                    />
                    <Area
                        type='monotone'
                        dataKey='value1'
                        stroke='#ED2328'
                        strokeWidth={2}
                        fill='url(#vehiclesGradient)'
                    />
                    <Area
                        type='monotone'
                        dataKey='value2'
                        stroke='#40AFFF'
                        strokeWidth={2}
                        fill='url(#devicesGradient)'
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}

export default Charts
